import {useMemo} from 'react';

import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsGraphMetric,
  AnalyticsMetricError,
  AnalyticsMultiGraphMetric,
  AnalyticsTableMetric,
} from '../../../../models/api/analyticsMetricsModel';
import {makeGetAnalyticsMetric} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {isComputedGraphMetric} from '../../../helpers/analyticsReportHelpers';
import {getComputedMetricTransformer} from '../../../transformers/analyticsMetricsTransformers';
import {AnyGraphMetric, AnyGraphMetricsEnum} from '../helperTypes';

export interface MockedGraphMetricData<T> {
  values: ReadonlyArray<T>;
  isMocked: boolean;
}

interface MockedData<T> {
  isMocked: true;
  values: ReadonlyArray<T>;
}

export function useGraphMetricData<T, U extends AnyGraphMetric>(
  selectedMetric: AnyGraphMetricsEnum | undefined,
  loadingStatePredicate: (metricData?: U) => boolean,
  mockData: ReadonlyArray<T>,
): [U | MockedData<T> | undefined, AnalyticsMetricError | undefined] {
  const localize = useLocalize();

  const [storeMetricData, metricError] = useAppSelector(
    makeGetAnalyticsMetric(
      isComputedGraphMetric(selectedMetric)
        ? getComputedMetricTransformer(selectedMetric).from
        : selectedMetric,
    ),
  );

  const graphMetricData = useMemo(
    () =>
      isComputedGraphMetric(selectedMetric)
        ? getComputedMetricTransformer(selectedMetric).transformer(
            storeMetricData as AnalyticsTableMetric | undefined,
            localize,
          )
        : (storeMetricData as AnalyticsGraphMetric | AnalyticsMultiGraphMetric | undefined),
    [localize, selectedMetric, storeMetricData],
  ) as U | undefined;

  if (!metricError && loadingStatePredicate(graphMetricData)) {
    return [
      {
        values: mockData,
        isMocked: true,
      },
      undefined,
    ];
  }

  return [graphMetricData, metricError];
}
